// RU-OPTUI: shared display helpers for option chains (display only; no order logic).

// OU-1: the option underlyings the backend serves. Indices = angelone_options.INDEX_SYMBOLS; stocks =
// angelone_options.stock_universe() (the stocks of market_data.SYMBOLS) in SYMBOLS order. A backend test
// (tests/test_ru_optui.py) parses this list and asserts it matches the backend.
export const OPTION_INDEX_SYMBOLS = ["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY"];
export const OPTION_STOCK_SYMBOLS = [
  "RELIANCE", "TCS", "HDFCBANK", "INFY", "ICICIBANK", "SBIN", "AXISBANK", "KOTAKBANK", "BAJFINANCE", "WIPRO",
  "HINDUNILVR", "TATAMOTORS", "TATASTEEL", "MARUTI", "SUNPHARMA", "LT", "ADANIENT", "HCLTECH", "TITAN",
];
export const OPTION_SYMBOLS = [...OPTION_INDEX_SYMBOLS, ...OPTION_STOCK_SYMBOLS];

// OU-3: a live chain older than the paper fill's fresh-quote bound (market_data.PAPER_QUOTE_MAX_AGE_S) is stale.
export const CHAIN_STALE_AFTER_S = 180;

const NON_LIVE = {
  mock: "Simulated — not tradable",
  busy: "Busy — no live chain",
  budget: "Budget used — no live chain",
  rate_limited_shared: "Rate limited — no live chain",
  stale_cache: "Stale cache — not tradable",
};

export function formatAge(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

/** Age in seconds of a chain's `as_of` (ISO with offset) at `nowMs`, or null when absent/unparseable. */
export function chainAgeSeconds(asOf, nowMs = Date.now()) {
  if (!asOf) return null;
  const t = Date.parse(asOf);
  if (Number.isNaN(t)) return null;
  return (nowMs - t) / 1000;
}

/** The clock part (HH:MM:SS) of the backend's IST `as_of` string, as written by the backend. */
export function asOfClock(asOf) {
  const m = /T(\d{2}:\d{2}:\d{2})/.exec(String(asOf || ""));
  return m ? m[1] : null;
}

/**
 * OU-3: how a chain's own `source` / `as_of` is shown.
 * Returns { kind, label, tone: "live"|"warn"|"bad", tradable, age, clock }.
 * `chain === null` with `error` set means the request failed.
 */
export function chainSourceInfo(chain, { error = false, nowMs = Date.now() } = {}) {
  if (error) return { kind: "error", label: "Chain unavailable", tone: "bad", tradable: false, age: null, clock: null };
  if (!chain) return { kind: "loading", label: "Loading chain…", tone: "warn", tradable: false, age: null, clock: null };
  const src = chain.source;
  if (src === "live") {
    const ageS = chainAgeSeconds(chain.as_of, nowMs);
    const stale = ageS != null && ageS > CHAIN_STALE_AFTER_S;
    return {
      kind: stale ? "stale" : "live",
      label: stale ? "Live (stale)" : "Live",
      tone: stale ? "warn" : "live",
      tradable: true,
      age: formatAge(ageS),
      clock: asOfClock(chain.as_of),
    };
  }
  const label = NON_LIVE[src] || "Unknown source — not tradable";
  return { kind: src || "unknown", label, tone: "bad", tradable: false, age: null, clock: null };
}

/** OU-5: the strikes a paper OPT ticket may pick: the live chain's rows only, else none. */
export function liveStrikes(chain) {
  if (!chain || chain.source !== "live") return [];
  return (chain.rows || []).map((r) => r.strike).filter((s) => s !== null && s !== undefined);
}

// RU-OPTSTALE OS-3: why an order may not be priced from this chain now, else null. The rule is the backend's
// (`freshness` from GET /market/options-chain = paper_trading.option_chain_freshness, the paper fill's own rule);
// this only ages a ready answer by the time since it was received (`receivedAtMs`, the client clock, so no
// client/server clock skew), and blocks once that age passes the backend's `max_age_s` or `ready_for_s` runs out.
const FRESHNESS_REASON = {
  not_live: "No live chain — order controls disabled",
  session_closed: "Market closed — the chain is a closed-session snapshot, not a live quote",
  no_as_of: "Chain quote time unknown (no as_of) — refresh the chain",
  freshness_error: "Chain freshness could not be checked — refresh the chain",
};

const staleText = (ageS, maxS) =>
  `Chain quotes are ${Math.round(ageS)}s old (max ${Math.round(maxS)}s) — refresh the chain`;

export function chainOrderBlock(chain, { error = false, nowMs = Date.now(), receivedAtMs = null } = {}) {
  const info = chainSourceInfo(chain, { error, nowMs });
  if (info.kind === "error") return "Chain unavailable — order controls disabled";
  if (info.kind === "loading") return "Loading chain…";
  if (!info.tradable) return `No live chain — ${info.label}`;
  // OS-3b: a chain the backend flags stale (RU-MCXCHAINQ MQ-7) prices nothing, with or without `freshness`
  if (chain.stale === true) {
    const a = Number(chain.age_s);
    return `Chain quotes marked stale${Number.isFinite(a) ? ` (${Math.round(a)}s old)` : ""} — refresh the chain`;
  }
  const f = chain.freshness;
  if (!f || typeof f !== "object") {
    // An older backend without `freshness`: the chain's own as_of age against the RU-OPTUI bound.
    const ageS = chainAgeSeconds(chain.as_of, nowMs);
    if (ageS == null) return FRESHNESS_REASON.no_as_of;
    return ageS > CHAIN_STALE_AFTER_S ? staleText(ageS, CHAIN_STALE_AFTER_S) : null;
  }
  const maxS = Number.isFinite(Number(f.max_age_s)) ? Number(f.max_age_s) : CHAIN_STALE_AFTER_S;
  if (f.order_ready !== true) {
    if (f.reason === "stale" && f.quote_age_s != null) return staleText(Number(f.quote_age_s), maxS);
    return FRESHNESS_REASON[f.reason] || "Chain is not order-ready — refresh the chain";
  }
  // OS-3a: a ready answer without a numeric quote_age_s is not ready (never read as age 0)
  if (f.quote_age_s == null || f.quote_age_s === "" || !Number.isFinite(Number(f.quote_age_s))) {
    return FRESHNESS_REASON.no_as_of;
  }
  const elapsedS = receivedAtMs == null ? null : Math.max(0, (nowMs - receivedAtMs) / 1000);
  const ageS = elapsedS == null ? chainAgeSeconds(chain.as_of, nowMs) : Number(f.quote_age_s) + elapsedS;
  if (ageS == null || !Number.isFinite(ageS)) return FRESHNESS_REASON.no_as_of;
  if (ageS > maxS) return staleText(ageS, maxS);
  const readyFor = Number.isFinite(Number(f.ready_for_s)) ? Number(f.ready_for_s) : maxS - Number(f.quote_age_s);
  if (elapsedS != null && elapsedS > readyFor) {
    return "Market session closed since the chain was fetched — refresh the chain";
  }
  return null;
}
